import { useState, type ChangeEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertCircle,
  ArrowLeft,
  Boxes,
  FileSpreadsheet,
  PlayCircle,
  Upload,
} from 'lucide-react';
import { useCurrentUser } from '../../hooks/useAuth';
import { useImportAssets, type AssetsImportReport } from '../../hooks/useAssets';
import { Button } from '../../components/ui/Button';
import { ApiError } from '../../lib/api';
import { ImportAssetsReportPanel } from './ImportAssetsReport';

const SAMPLE_CSV = `asset_tag,description,station,category,serial,registration,imei,mac_address,syscode,notes,assigned_to_email,confirm_on_arrival
LT-COR-2999,Dell Latitude 5450 14-inch,IT,Laptop,SN-XYZ-001,,,aa:bb:cc:dd:ee:01,PLN-004221,,jamie.reed@example.com,
PH-COR-1042,iPhone SE 3rd gen,IT,Phone,,,352001234567890,,,,jamie.reed@example.com,
SIM-COR-0871,EE 5G data SIM,IT,SIM,,,,,,Activated,jamie.reed@example.com,
VEH-AB23XYZ,Ford Transit Custom 2024,Fleet,Van,,AB23 XYZ,,,PLN-007630,,,
DRILL-TUPE-01,Makita drill (existing),Tools,Power tool,SN-DRILL-88,,,,,,jamie.reed@example.com,Yes
`;

export function ImportAssets() {
  const { data: user, isLoading } = useCurrentUser();
  const importMutation = useImportAssets();
  const navigate = useNavigate();
  const [csv, setCsv] = useState<string | null>(null);
  const [filename, setFilename] = useState<string | null>(null);
  const [filesize, setFilesize] = useState<number | null>(null);
  const [dryRun, setDryRun] = useState(true);
  const [report, setReport] = useState<AssetsImportReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (isLoading)
    return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!user) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Please <Link to="/login" className="underline">sign in</Link>.
      </main>
    );
  }
  const allowed = user.roles.some((r) =>
    ['admin', 'super_admin', 'coordinator'].includes(r),
  );
  if (!allowed) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        You do not have permission to import assets.
      </main>
    );
  }

  function onFile(e: ChangeEvent<HTMLInputElement>) {
    setError(null);
    setReport(null);
    const file = e.target.files?.[0];
    if (!file) return;
    setFilename(file.name);
    setFilesize(file.size);
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result ?? ''));
    reader.onerror = () => setError('failed to read file');
    reader.readAsText(file);
  }

  async function onSubmit(opts: { force_commit?: boolean } = {}) {
    if (!csv) return;
    const isDry = opts.force_commit ? false : dryRun;
    setError(null);
    try {
      const r = await importMutation.mutateAsync({ csv, dry_run: isDry });
      setReport(r);
      if (!isDry) {
        toast.success(
          `Imported ${r.inserted} new asset${r.inserted === 1 ? '' : 's'}` +
            (r.skipped > 0 ? `, skipped ${r.skipped}` : '') +
            ((r.syscode_updated ?? 0) > 0
              ? `, updated ${r.syscode_updated} syscode${r.syscode_updated === 1 ? '' : 's'}`
              : ''),
        );
        navigate('/admin/assets');
      }
    } catch (err) {
      setReport(null);
      const msg = err instanceof ApiError ? err.message : 'import failed';
      setError(msg);
      toast.error(msg);
    }
  }

  function downloadSample() {
    const blob = new Blob([SAMPLE_CSV], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'assets-sample.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-8">
        <Link
          to="/admin/assets"
          className="inline-flex items-center gap-1 text-xs font-medium text-fg-muted hover:text-fg"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to equipment
        </Link>

        <header className="mt-4 flex items-start gap-4">
          <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-accent/10 text-accent">
            <Boxes className="h-6 w-6" strokeWidth={2} />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-accent">
              Pre-day
            </div>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              Import assets
            </h1>
            <p className="mt-1 text-sm text-fg-muted">
              Bulk-load the asset register from a CSV. Required columns:{' '}
              <code className="mx-0.5 rounded bg-surface-2 px-1 text-xs">asset_tag</code>,{' '}
              <code className="mx-0.5 rounded bg-surface-2 px-1 text-xs">description</code>,{' '}
              <code className="mx-0.5 rounded bg-surface-2 px-1 text-xs">station</code>.
              Optional:{' '}
              <code className="mx-0.5 rounded bg-surface-2 px-1 text-xs">category</code>,{' '}
              <code className="mx-0.5 rounded bg-surface-2 px-1 text-xs">serial</code>,{' '}
              <code className="mx-0.5 rounded bg-surface-2 px-1 text-xs">registration</code>{' '}
              (vehicle reg for Fleet),{' '}
              <code className="mx-0.5 rounded bg-surface-2 px-1 text-xs">imei</code>{' '}
              (phones / SIMs),{' '}
              <code className="mx-0.5 rounded bg-surface-2 px-1 text-xs">mac_address</code>,{' '}
              <code className="mx-0.5 rounded bg-surface-2 px-1 text-xs">syscode</code>{' '}
              (the CAFM asset code, unique),{' '}
              <code className="mx-0.5 rounded bg-surface-2 px-1 text-xs">notes</code>,{' '}
              <code className="mx-0.5 rounded bg-surface-2 px-1 text-xs">assigned_to_email</code>{' '}
              (pre-assigns the asset to that attendee),{' '}
              <code className="mx-0.5 rounded bg-surface-2 px-1 text-xs">confirm_on_arrival</code>{' '}
              (yes/no; a TUPE starter already holds this item, so the desk confirms it rather than
              issuing). Re-uploading the same tag is skipped (idempotent), except that a syscode on
              a skipped row still updates that asset, so the CAFM team's enriched sheet can backfill
              codes later.
            </p>
            <p className="mt-2 text-xs text-fg-muted">
              <strong>Category tip:</strong> use one of the canonical
              values (Laptop, Desktop, Monitor, Phone, Tablet, SIM,
              Headset, Dock, Webcam, Charger, Cable, Van, Car, Truck,
              Trailer, Boots, Hi-vis, Helmet, Gloves, Trousers, Jacket,
              Eye protection, Ear protection, Mask, Drill, Hand tools,
              Ladder, Toolbox, Power tool, Document) so the kit-issue
              form shows the right field labels (Serial / IMEI /
              Registration / Size). Free text still works for one-offs.
            </p>
            <button
              type="button"
              onClick={downloadSample}
              className="mt-2 text-xs font-medium text-fg-muted underline-offset-2 hover:underline"
            >
              Download sample CSV
            </button>
          </div>
        </header>

        <section className="mt-6 rounded-lg border border-line/10 bg-surface p-5 shadow-sm sm:p-6">
          <label
            htmlFor="csv-file"
            className={`flex cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors ${
              csv
                ? 'border-success/25 bg-success-soft/15'
                : 'border-line-strong/20 bg-surface-2 hover:bg-surface-2'
            }`}
          >
            {csv ? (
              <>
                <FileSpreadsheet className="h-8 w-8 text-emerald-600" />
                <div>
                  <div className="text-sm font-semibold text-success-soft-fg">
                    {filename}
                  </div>
                  <div className="mt-0.5 text-xs text-success-soft-fg">
                    {filesize != null ? `${(filesize / 1024).toFixed(1)} KB` : null}{' '}
                    · click to choose a different file
                  </div>
                </div>
              </>
            ) : (
              <>
                <Upload className="h-8 w-8 text-fg-dim" />
                <div>
                  <div className="text-sm font-semibold">Choose a CSV file</div>
                  <div className="mt-0.5 text-xs text-fg-muted">
                    Or drag and drop a file here
                  </div>
                </div>
              </>
            )}
            <input
              id="csv-file"
              type="file"
              accept=".csv,text/csv"
              onChange={onFile}
              className="sr-only"
            />
          </label>

          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
                className="h-4 w-4"
              />
              <span>
                Dry run{' '}
                <span className="text-xs text-fg-muted">
                  (validate and report; do not write)
                </span>
              </span>
            </label>
            <Button
              variant="primary"
              size="md"
              iconLeft={<PlayCircle className="h-4 w-4" />}
              loading={importMutation.isPending}
              disabled={!csv || importMutation.isPending}
              onClick={() => onSubmit()}
            >
              {dryRun ? 'Run dry run' : 'Commit import'}
            </Button>
          </div>
        </section>

        {error ? (
          <div className="mt-4 flex items-start gap-2 rounded-md bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}

        {report ? (
          <ImportAssetsReportPanel
            report={report}
            onCommit={() => {
              setDryRun(false);
              void onSubmit({ force_commit: true });
            }}
          />
        ) : null}
      </div>
    </main>
  );
}
